import { useTitle } from '@/hooks/useTitle';
import styles from './styles.module.scss';

export function CookiesPage() {
  useTitle('DienTEA | Cookies');

  return (
    <main className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <h1>Política de Cookies</h1>
          <p className={styles.subtitle}>Última actualización: mayo de 2026</p>
        </header>

        <section className={styles.section}>
          <div className={styles.notice}>
            <strong>Resumen:</strong> Este sitio web no utiliza cookies propias. Sin embargo, al reproducir vídeos de
            YouTube incrustados, el servicio de Google/YouTube puede establecer cookies de terceros en tu navegador.
          </div>
        </section>

        <section className={styles.section}>
          <h2>1. ¿Qué es una cookie?</h2>
          <p>
            Una cookie es un pequeño fichero de texto que un servidor web almacena en el dispositivo del usuario al
            visitar una página web. Pueden ser de <strong>sesión</strong> (se eliminan al cerrar el navegador) o{' '}
            <strong>persistentes</strong> (permanecen hasta una fecha de caducidad definida), así como{' '}
            <strong>propias</strong> o <strong>de terceros</strong>.
          </p>
        </section>

        <section className={styles.section}>
          <h2>2. Cookies propias</h2>
          <p>
            Este sitio web <strong>no instala ninguna cookie propia</strong>. No se utilizan cookies de análisis,
            publicidad, seguimiento ni preferencias de usuario generadas por nuestra plataforma.
          </p>
        </section>

        <section className={styles.section}>
          <h2>3. Cookies de terceros: YouTube (Google LLC)</h2>
          <p>
            Nuestro sitio incluye reproductores de vídeo incrustados de <strong>YouTube</strong>, propiedad de Google
            LLC. Al acceder a una página con estos reproductores, YouTube puede instalar cookies de terceros en tu
            dispositivo. Estas cookies son gestionadas exclusivamente por Google y están sujetas a su propia política de
            privacidad.
          </p>
          <p>
            Dependiendo de si tienes sesión iniciada en una cuenta de Google, estas cookies pueden pertenecer a alguna
            de las siguientes categorías:
          </p>

          <div className={styles.categories}>
            <div className={styles.category}>
              <span className={styles.categoryLabel}>Autenticación y sesión</span>
              <p>
                Identifican y verifican al usuario dentro del servicio de YouTube durante la navegación. Se eliminan al
                cerrar el navegador o tienen una caducidad de hasta 2 años si el usuario tiene sesión iniciada en
                Google.
              </p>
            </div>
            <div className={styles.category}>
              <span className={styles.categoryLabel}>Seguridad</span>
              <p>
                Protegen la sesión del usuario frente a accesos no autorizados y ayudan a detectar actividad
                fraudulenta. Caducidad de entre 6 meses y 1 año.
              </p>
            </div>
            <div className={styles.category}>
              <span className={styles.categoryLabel}>Preferencias y funcionalidad</span>
              <p>
                Almacenan ajustes del reproductor como el volumen, la calidad del vídeo o el idioma de subtítulos para
                mejorar la experiencia de reproducción.
              </p>
            </div>
            <div className={styles.category}>
              <span className={styles.categoryLabel}>Analítica</span>
              <p>
                Recogen métricas de uso del reproductor, como el número de reproducciones o el tiempo de visualización,
                con fines estadísticos internos de YouTube.
              </p>
            </div>
            <div className={styles.category}>
              <span className={styles.categoryLabel}>Publicidad y personalización</span>
              <p>
                Si el usuario tiene sesión iniciada en Google, pueden utilizarse para mostrar anuncios relevantes
                basados en su historial de navegación y preferencias.
              </p>
            </div>
          </div>

          <p>
            Para consultar el detalle actualizado de las cookies que establece YouTube, puedes acudir directamente a la{' '}
            <a href='https://policies.google.com/privacy?hl=es' target='_blank' rel='noopener noreferrer'>
              Política de Privacidad de Google
            </a>{' '}
            y a las{' '}
            <a href='https://policies.google.com/technologies/cookies?hl=es' target='_blank' rel='noopener noreferrer'>
              Tecnologías de cookies de Google
            </a>
            .
          </p>
        </section>

        <section className={styles.section}>
          <h2>4. Base jurídica</h2>
          <p>Esta política se elabora en cumplimiento de:</p>
          <ul className={styles.list}>
            <li>Reglamento (UE) 2016/679 — RGPD</li>
            <li>Directiva 2002/58/CE sobre privacidad en comunicaciones electrónicas</li>
            <li>Ley Orgánica 3/2018 — LOPDGDD (España)</li>
            <li>Ley 34/2002 — LSSI-CE (España)</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>5. Tus derechos</h2>
          <p>
            Puedes ejercer tus derechos de acceso, rectificación, supresión, limitación, portabilidad y oposición frente
            a Google en el{' '}
            <a href='https://myaccount.google.com/data-and-privacy' target='_blank' rel='noopener noreferrer'>
              Centro de privacidad de Google
            </a>
            . Para reclamaciones ante la autoridad española de protección de datos, puedes dirigirte a la{' '}
            <a href='https://www.aepd.es' target='_blank' rel='noopener noreferrer'>
              AEPD
            </a>
            .
          </p>
        </section>

        <section className={styles.section}>
          <h2>6. Cómo gestionar las cookies</h2>
          <p>Puedes bloquear o eliminar cookies desde la configuración de tu navegador:</p>
          <ul className={styles.list}>
            <li>
              <strong>Chrome</strong> — Configuración → Privacidad y seguridad → Cookies y otros datos de sitios
            </li>
            <li>
              <strong>Firefox</strong> — Preferencias → Privacidad y seguridad → Cookies y datos del sitio
            </li>
            <li>
              <strong>Safari</strong> — Preferencias → Privacidad → Gestionar datos del sitio web
            </li>
            <li>
              <strong>Edge</strong> — Configuración → Cookies y permisos del sitio → Cookies y datos del sitio
            </li>
          </ul>
          <p>
            Ten en cuenta que bloquear las cookies de YouTube puede impedir la reproducción de los vídeos incrustados en
            este sitio.
          </p>
        </section>

        <section className={styles.section}>
          <h2>7. Contacto</h2>
          <p>
            Si tienes cualquier pregunta sobre esta política, puedes contactarnos en{' '}
            <a href='mailto:contacto@dientea.es'>contacto@dientea.es</a>.
          </p>
        </section>
      </div>
    </main>
  );
}
